// components/TrialBalance.jsx
import React, { useState, useEffect } from 'react';
import generalLedgerAPI from '../api/generalLedgerAPI';
import { useAuth } from '../context/AuthContext';
import ReportToolbar from '../shared/ReportToolbar';
import { computePeriodRange } from '../shared/PeriodSelect';
import Button from './Button';

const TrialBalance = () => {
  const { selectedClient } = useAuth();
  const [trialBalance, setTrialBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const accountingClientId = selectedClient?._id || localStorage.getItem('accountingClientId') || null;
  const [fiscalYear, setFiscalYear] = useState(null);
  // Trial Balance is a point-in-time report — the period preset's end date is
  // used as the "as of" date; the start date isn't meaningful here.
  const [period, setPeriod] = useState(() => ({ preset: 'YTD', ...computePeriodRange('YTD') }));
  const asOfDate = period.endDate;

  useEffect(() => {
    if (accountingClientId) {
      const fy = localStorage.getItem(`activeFiscalYear:${accountingClientId}`);
      if (fy) setFiscalYear(fy);
    }
  }, [accountingClientId]);

  useEffect(() => {
    const onFYChange = () => {
      const id = localStorage.getItem('accountingClientId');
      if (id) {
        const fy = localStorage.getItem(`activeFiscalYear:${id}`);
        if (fy) setFiscalYear(fy);
      }
    };
    window.addEventListener('fiscalYear:changed', onFYChange);
    return () => window.removeEventListener('fiscalYear:changed', onFYChange);
  }, []);

  const fetchTrialBalance = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await generalLedgerAPI.getTrialBalance(asOfDate, accountingClientId, fiscalYear);
      if (response.success) {
        setTrialBalance(response.data);
      } else {
        setError(response.message || 'Failed to fetch trial balance');
      }
    } catch (err) {
      setError(err.message || 'Error fetching trial balance');
      console.error('Trial Balance Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountingClientId) {
      fetchTrialBalance();
    }
  }, [accountingClientId, asOfDate, fiscalYear]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PHP',
    }).format(amount || 0);
  };

  const formatCurrencyForCSV = (amount) => {
    return (amount || 0).toFixed(2);
  };

  const isBalanced = trialBalance?.totals?.difference === 0;

  // CSV Export Function
  const exportToCSV = () => {
    if (!trialBalance) return;

    const csvData = [
      // Headers
      ['Account Number', 'Account Name', 'Account Type', 'Normal Balance', 'Debit Balance', 'Credit Balance', 'Net Balance'],
      
      // Data rows - FIXED: using displayBalances
      ...trialBalance.trialBalance.map(item => [
        item.account.accountNumber,
        `"${item.account.accountName}"`,
        item.account.accountType,
        item.account.normalBalance,
        formatCurrencyForCSV(item.displayBalances.debit),
        formatCurrencyForCSV(item.displayBalances.credit),
        formatCurrencyForCSV(item.displayBalances.debit - item.displayBalances.credit)
      ]),
      
      // Empty row
      [],
      
      // Totals
      ['', '', '', 'TOTALS:', formatCurrencyForCSV(trialBalance.totals.totalDebits), formatCurrencyForCSV(trialBalance.totals.totalCredits), formatCurrencyForCSV(trialBalance.totals.difference)],
      
      // Summary
      ['', '', '', 'DIFFERENCE:', '', '', formatCurrencyForCSV(trialBalance.totals.difference)],
      ['', '', '', 'STATUS:', '', '', isBalanced ? 'BALANCED' : 'OUT OF BALANCE']
    ];

    // Convert to CSV string
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `trial-balance-${asOfDate}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Detailed Analysis CSV Export
  const exportDetailedAnalysis = () => {
    if (!trialBalance) return;

    const csvData = [
      // Headers
      ['Account Number', 'Account Name', 'Account Type', 'Normal Balance', 
       'Debit Balance', 'Credit Balance', 'Net Balance', 
       'Balance Direction', 'Variance from Expected', 'Notes'],
      
      // Data rows with analysis - FIXED: using displayBalances
      ...trialBalance.trialBalance.map(item => {
        const netBalance = item.displayBalances.debit - item.displayBalances.credit; // FIXED
        const expectedDirection = item.account.normalBalance === 'debit' ? 'Debit' : 'Credit';
        const actualDirection = netBalance >= 0 ? 'Debit' : 'Credit';
        const variance = expectedDirection !== actualDirection ? 'WRONG DIRECTION' : '';
        
        let notes = '';
        if (Math.abs(netBalance) > 100000) {
          notes = 'LARGE BALANCE';
        } else if (item.account.normalBalance === 'debit' && netBalance < 0) {
          notes = 'Negative asset/expense';
        } else if (item.account.normalBalance === 'credit' && netBalance > 0) {
          notes = 'Positive liability/equity';
        }

        return [
          item.account.accountNumber,
          `"${item.account.accountName}"`,
          item.account.accountType,
          item.account.normalBalance,
          formatCurrencyForCSV(item.displayBalances.debit), // FIXED
          formatCurrencyForCSV(item.displayBalances.credit), // FIXED
          formatCurrencyForCSV(netBalance),
          actualDirection,
          variance,
          notes
        ];
      }),
      
      // Empty row
      [],
      
      // Summary section
      ['SUMMARY ANALYSIS', '', '', '', '', '', '', '', '', ''],
      ['Total Debits', '', '', '', formatCurrencyForCSV(trialBalance.totals.totalDebits), '', '', '', '', ''],
      ['Total Credits', '', '', '', '', formatCurrencyForCSV(trialBalance.totals.totalCredits), '', '', '', ''],
      ['Difference', '', '', '', '', '', formatCurrencyForCSV(trialBalance.totals.difference), '', '', ''],
      ['Status', '', '', '', '', '', '', '', '', isBalanced ? 'BALANCED' : 'OUT OF BALANCE'],
      
      // Problem accounts
      [],
      ['PROBLEM ACCOUNTS ANALYSIS', '', '', '', '', '', '', '', '', ''],
      ...trialBalance.trialBalance
        .filter(item => {
          const netBalance = item.displayBalances.debit - item.displayBalances.credit; // FIXED
          return (item.account.normalBalance === 'debit' && netBalance < 0) ||
                 (item.account.normalBalance === 'credit' && netBalance > 0) ||
                 Math.abs(netBalance) > 100000;
        })
        .map(item => [
          item.account.accountNumber,
          `"${item.account.accountName}"`,
          item.account.accountType,
          item.account.normalBalance,
          formatCurrencyForCSV(item.displayBalances.debit), // FIXED
          formatCurrencyForCSV(item.displayBalances.credit), // FIXED
          formatCurrencyForCSV(item.displayBalances.debit - item.displayBalances.credit), // FIXED
          'CHECK',
          'POTENTIAL ISSUE',
          'Review this account'
        ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `trial-balance-analysis-${asOfDate}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">TRIAL BALANCE</h1>
          <ReportToolbar
            period={period}
            onPeriodChange={setPeriod}
            fiscalYear={fiscalYear}
            onExportCSV={trialBalance ? exportToCSV : undefined}
            onRefresh={fetchTrialBalance}
            refreshing={loading}
            extra={trialBalance ? (
              <Button variant="ghost" size="sm" onClick={exportDetailedAnalysis}>
                Detailed analysis
              </Button>
            ) : undefined}
          />
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Balance Status */}
        {trialBalance && (
          <div className={`mb-8 rounded-md p-4 text-center font-bold text-lg ${
            isBalanced ? 'bg-green-50 border border-green-200 text-green-900' : 'bg-red-50 border border-red-200 text-red-900'
          }`}>
            {isBalanced ? '✓ BALANCED' : '✗ OUT OF BALANCE'}
            {!isBalanced && ` - Difference: ${formatCurrency(trialBalance.totals.difference)}`}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          </div>
        )}

        {/* Trial Balance Content */}
        {!loading && trialBalance && (
          <div className="bg-white p-12 font-mono text-sm max-w-3xl mx-auto">
            {/* Column Headers */}
            <div className="flex justify-between mb-4 font-bold text-gray-900 border-b border-gray-300 pb-2">
              <span>Account</span>
              <span className="w-32 text-right">Debit</span>
              <span className="w-32 text-right">Credit</span>
            </div>

            {/* Account Rows */}
            <div className="space-y-0 mb-6">
              {trialBalance.trialBalance?.map((item, idx) => (
                <div key={`tb-${item.account._id || item.account.accountNumber || idx}`} className="flex justify-between py-1 text-gray-900">
                  <div className="flex-1">
                    <span className="font-semibold">{item.account.accountNumber}</span>
                    <span className="ml-4">{item.account.accountName}</span>
                  </div>
                  <span className="w-32 text-right font-mono">
                    {item.displayBalances.debit > 0 ? formatCurrency(item.displayBalances.debit) : '-'}
                  </span>
                  <span className="w-32 text-right font-mono">
                    {item.displayBalances.credit > 0 ? formatCurrency(item.displayBalances.credit) : '-'}
                  </span>
                </div>
              ))}
            </div>

            {/* Totals Section */}
            <div className="border-t-2 border-gray-900 pt-4 space-y-2">
              <div className="flex justify-between font-bold text-gray-900">
                <span>TOTALS</span>
                <span className="w-32 text-right border-b border-gray-900">{formatCurrency(trialBalance.totals.totalDebits)}</span>
                <span className="w-32 text-right border-b border-gray-900">{formatCurrency(trialBalance.totals.totalCredits)}</span>
              </div>
              <div className="flex justify-between text-gray-900 border-b-2 border-gray-900 pb-2">
                <span className="font-semibold">Net Difference</span>
                <span className="w-32"></span>
                <span className="w-32 text-right font-mono">{formatCurrency(Math.abs(trialBalance.totals.difference))}</span>
              </div>
            </div>

          </div>
        )}

        {!loading && !trialBalance && !error && (
          <div className="text-center text-gray-500 py-12">
            Select a date to view the trial balance
          </div>
        )}
      </div>
    </div>
  );
};

export default TrialBalance;